import { serverHost } from "../../../../http_common.js";
import { useState, useRef, useEffect } from "react";
import "./registerDetailsComp.css"; // Import the CSS file

import { useReactToPrint } from "react-to-print";
import { useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";
import axios from "axios";
import Logo from "../test.png";
import AssuredWorkloadIcon from "@mui/icons-material/AssuredWorkload";

import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";


export default function RegisterDetailsComp() {
  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef });
  const { accessType } = useSelector((state) => state.clientFunction);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [transactionsData, setTransactionsData] = useState([]);
  const [loadError, setLoadError] = useState("");
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { registerData } = useSelector((state) => state.terminalActions);

  const { clientData, userInfo } = useSelector(
    (state) => state.clientFunction?.companiesDB,
  );

  async function apiGetSales() {
    if (!registerData?.registerId || !clientData?.clientId) return;
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await axios.get(`${serverHost}/register/${encodeURIComponent(registerData.registerId)}/client/${encodeURIComponent(clientData.clientId)}/getRegSoldItem`, { timeout: 20000 });
      if (![200, 201].includes(data?.status)) throw new Error(data?.message || "Unable to load register payments.");
      setTransactionsData(Array.isArray(data?.regTransactions) ? data.regTransactions : []);
    } catch (error) {
      setLoadError(error.response?.data?.message || error.message || "Unable to load payments.");
      setTransactionsData([]);
    } finally { setLoading(false); }
  }

  // SUMMATION OF THE ENTIRE TRANSACTIONS FUCTIONS
  function getSalesSummary(rows = []) {
    const methods = ["cash", "credit", "debit", "gift", "account", "cheque", "multiple-pay"];
    const summary = {
      totalItemsSold: 0, totalPaymentsMade: 0, totalDebts: 0,
      totalSales: 0, totalTax: 0, totalDiscount: 0, cashCollected: 0,
      paymentMethods: Object.fromEntries(methods.map(m => [m, { transactions: 0, amount: 0 }])),
    };
    for (const original of rows) {
      const transaction = original?.soldItemPayload || original;
      if (!transaction) continue;
      const total = Number(transaction.totals?.total ?? transaction.totals?.subtotal ?? 0);
      if (!Number.isFinite(total)) continue;
      const methodRaw = String(transaction.payment?.method || "").toLowerCase();
      const method = methodRaw === "check" ? "cheque" : methodRaw === "multiple" ? "multiple-pay" : methodRaw;
      summary.totalItemsSold += (transaction.soldItems || []).reduce((sum, item) => sum + Number(item.soldQuantity || 0), 0);
      summary.totalSales += total;
      summary.totalTax += Number(transaction.totals?.taxAmount || 0);
      summary.totalDiscount += Number(transaction.totals?.discountAmount || 0);
      if (summary.paymentMethods[method]) {
        summary.paymentMethods[method].transactions += 1;
        summary.paymentMethods[method].amount += total;
      }
      if (method === "multiple-pay") {
        for (const payment of transaction.payment?.payments || []) {
          const amount = Number(payment?.amount || 0);
          if (!Number.isFinite(amount) || amount < 0) continue;
          const option = String(payment?.method || "").toLowerCase();
          if (option === "credit") summary.totalDebts += amount;
          else if (["cash", "account", "debit"].includes(option)) summary.totalPaymentsMade += amount;
          if (option === "cash") summary.cashCollected += amount;
        }
      } else if (method === "credit") summary.totalDebts += total;
      else if (["cash", "account", "debit"].includes(method)) summary.totalPaymentsMade += total;
      if (method === "cash") summary.cashCollected += total;
    }
    return summary;
  }
  const salesSummary = getSalesSummary(transactionsData);

  // Export the same currently loaded register report as a downloadable PDF.
  // Keep this separate from printing so users can choose either action.
  function downloadRegisterPdf() {
    if (loading || loadError) return;
    try {
      const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const money = (value) => `NGN ${Number(value || 0).toLocaleString("en-NG", { maximumFractionDigits: 2 })}`;
      const safeText = (value) => String(value ?? "-");
      const ref = safeText(registerData?.registerId);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(17);
      doc.text("Register Details", 14, 19);
      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      const details = [
        ["Register Reference ID", ref],
        ["Representative", safeText(registerData?.representiveName)],
        ["Staff ID", safeText(registerData?.representiveId)],
        ["Username", safeText(registerData?.representiveUserName)],
        ["Inventory", safeText(registerData?.inventory?.inventoryName)],
        ["Location", safeText(registerData?.location?.name)],
        ["Session Start", new Date(registerData?.timeInn || Date.now()).toLocaleString()],
        ["Report Generated", new Date().toLocaleString()],
        ["Saved Sales", String(transactionsData.length)],
      ];
      autoTable(doc, {
        startY: 27,
        head: [["Operation Details", "Value"]],
        body: details,
        theme: "grid",
        styles: { fontSize: 9, cellPadding: 2.5 },
        headStyles: { fillColor: [25, 65, 112] },
        margin: { left: 14, right: 14 },
      });
      const paymentRows = [
        ["Total items sold", String(salesSummary.totalItemsSold)],
        ["Total payments", money(salesSummary.totalPaymentsMade)],
        ["Total debts", money(salesSummary.totalDebts)],
        ["Total sales", money(salesSummary.totalSales)],
        ["Sales tax", money(salesSummary.totalTax)],
        ["Discount", money(salesSummary.totalDiscount)],
        ["Cash received", money(salesSummary.cashCollected)],
        ["Opening balance", money(registerData?.openingBalance)],
        ["Expected cash", money(Number(registerData?.openingBalance || 0) + salesSummary.cashCollected)],
        ["Cash difference", registerData?.cashDifference == null ? "Not reconciled" : money(registerData.cashDifference)],
      ];
      for (const [method, details] of Object.entries(salesSummary.paymentMethods)) {
        paymentRows.push([`${method.replace(/\b\w/g, (char) => char.toUpperCase())} (${details.transactions} sales)`, money(details.amount)]);
      }
      autoTable(doc, {
        startY: (doc.lastAutoTable?.finalY || 27) + 9,
        head: [["Payment Summary", "Amount / Count"]],
        body: paymentRows,
        theme: "striped",
        styles: { fontSize: 9, cellPadding: 2.5 },
        headStyles: { fillColor: [25, 65, 112] },
        margin: { left: 14, right: 14 },
      });
      const pages = doc.internal.getNumberOfPages();
      for (let page = 1; page <= pages; page++) {
        doc.setPage(page);
        doc.setFontSize(8);
        doc.setTextColor(110);
        doc.text(`Register ${ref}  |  Page ${page} of ${pages}`, 14, 287);
      }
      const safeRef = ref.replace(/[^a-z0-9_-]/gi, "-").slice(0, 64) || "register";
      doc.save(`register-details-${safeRef}.pdf`);
    } catch (error) {
      enqueueSnackbar("Unable to export register details as PDF. Please try again.", { variant: "error" });
    }
  }

  // SESSION FUNCTIONS

  const diffMs = Math.max(0, Date.now() - new Date(registerData?.timeInn || Date.now()).getTime());

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  const sessionTime = `${hours}h ${minutes}m`;

  const sessionStart = new Date(registerData?.timeInn || Date.now());
  const from = sessionStart.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const to = new Date().toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });

  const theSession = `${from} - ${to}`;
  useEffect(() => {
    apiGetSales();
  }, [registerData?.registerId, clientData?.clientId]);

  return (
    <div
      id="registerDetailTransactions"
      className="registerDetailWrapper fx-cl space3"
      onClick={(e) => e.stopPropagation()}
      ref={contentRef}
    >
      <div className="register-export-actions" aria-label="Register report export actions">
        <button
          type="button"
          className="register-export-action"
          onClick={() => reactToPrintFn()}
          disabled={loading || !!loadError}
          title="Print register details"
        >
          <LocalPrintshopIcon fontSize="small" /> Print
        </button>
        <button
          type="button"
          className="register-export-action"
          onClick={downloadRegisterPdf}
          disabled={loading || !!loadError}
          title="Download register details as PDF"
        >
          <PictureAsPdfIcon fontSize="small" /> Export PDF
        </button>
      </div>
      <div className="pos-report-status" aria-live="polite">
        <span>{loading ? "Loading register records…" : `${transactionsData.length} saved sales in this register`}</span>
        <button type="button" onClick={apiGetSales} disabled={loading}>Refresh</button>
        {loadError && <p role="alert" className="pos-inline-error">{loadError}</p>}
      </div>
      <div className="registerDetail flex-container">
        {/* First Container */}
        <div className="registerDetail-first-container">
          <div className="payments-left-side fx-ac space2">
            <figure className="paymentsLogo">
              <AssuredWorkloadIcon style={{ fontSize: "4.7rem" }} />
            </figure>
            <div className="fx-cl space1">
              <h1>Payments</h1>

              <p>Representative: {registerData?.representiveName}</p>
            </div>
          </div>
          <div className="payments-right-side fx-cl space1">
            <p>{registerData?.location?.name} </p>
            <p>{registerData?.location?.address}</p>
          </div>
        </div>

        {/* Second Container */}
        <div className="registerDetail-second-container fx-jb space3">
          <div className="registerDetail-inner-div">
            <div className=" fx-cl space1">
              <h3>Operation Details</h3>
              <div className="fx-cl spacem">
                <p>
                  <strong>Register Reference ID: </strong>
                  {registerData?.registerId}
                </p>
                <p>
                  <strong>Items sold: </strong>
                  {salesSummary.totalItemsSold}
                </p>
                <p>
                  <strong>Total payments: </strong> ₦
                  {salesSummary.totalPaymentsMade.toLocaleString()}
                </p>
                <p>
                  <strong>Debts: </strong> ₦
                  {salesSummary.totalDebts.toLocaleString()}
                </p>
                <p>
                  <strong>Session: </strong>
                  {theSession}
                </p>
                <p>
                  <strong>Hours: </strong> {sessionTime}
                </p>
              </div>
            </div>
          </div>
          <div className="registerDetail-inner-div">
            <div className=" fx-cl space1">
              <h3>User info:</h3>
              <div className="fx-cl spacem">
                <p>
                  <strong>Staff ID: </strong> {registerData?.representiveId}
                </p>
                <p>
                  <strong>Username: </strong>
                  {registerData?.representiveUserName}
                </p>

                <p>
                  <strong>Location: </strong>{" "}
                  {registerData?.inventory?.inventoryName}
                </p>
                <p>
                  <strong>Phone No.: </strong>{" "}
                  {registerData?.representiveNumber}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Third Container */}
        <div className="registerDetail-third-container fx-cl space3">
          <div className="fx-cl">
            <h3>Payment details</h3>
            <p>Overview of payments and outstanding balances</p>
          </div>
          <table className="registerDetailTable">
            <thead style={{ textAlign: "left" }}>
              <tr>
                <th>Serial No.</th>
                <th>Items</th>
                <th>Description</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan="4">
                  <h2>Earning</h2>
                </td>
              </tr>

              <tr>
                <td>01</td>
                <td>Total Items Sold</td>
                <td>All sold inventory items</td>
                <td>{salesSummary.totalItemsSold}</td>
              </tr>

              <tr>
                <td>02</td>
                <td>Total Payments</td>
                <td>Paid transactions excluding credit</td>
                <td>₦{salesSummary.totalPaymentsMade.toLocaleString()}</td>
              </tr>

              <tr>
                <td>03</td>
                <td>Total Debts</td>
                <td>Credit transactions</td>
                <td>₦{salesSummary.totalDebts.toLocaleString()}</td>
              </tr>

              <tr>
                <td colSpan="4">
                  <h2>Payment Methods</h2>
                </td>
              </tr>

              {Object.entries(salesSummary.paymentMethods).map(
                ([method, data], index) => (
                  <tr key={method}>
                    <td>{String(index + 1).padStart(2, "0")}</td>
                    <td>
                      {method.replace(/\b\w/g, (char) => char.toUpperCase())}
                    </td>
                    <td>{data.transactions} transaction(s)</td>
                    <td>₦{data.amount.toLocaleString()}</td>
                  </tr>
                ),
              )}

              <tr>
                <td></td>
                <td>
                  <strong>TOTAL</strong>
                </td>
                <td>-</td>
                <td>
                  <strong>
                    ₦{salesSummary.totalPaymentsMade.toLocaleString()}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>

          <div className="baseDetails fx-jb space6">
            <div className="buttomLeftDetails fx-cl space1">
              <span>
                <strong>Payment Summary</strong>
              </span>

              <div className="fx-cl space">
                <span>
                  Cash Payments:{" "}
                  <strong>
                    ₦{salesSummary.cashCollected.toLocaleString()}
                  </strong>
                </span>

                <span>
                  Bank Transfer:{" "}
                  <strong>
                    ₦
                    {salesSummary.paymentMethods.account.amount.toLocaleString()}
                  </strong>
                </span>

                <span>
                  Card Payments:{" "}
                  <strong>
                    ₦{salesSummary.paymentMethods.debit.amount.toLocaleString()}
                  </strong>
                </span>

                <span>
                  POS Terminal: <strong>Not tracked separately</strong>
                </span>

                <span>
                  Mobile Money: <strong>Not tracked separately</strong>
                </span>

                <span>
                  Credit Sales:{" "}
                  <strong>
                    ₦
                    {salesSummary.paymentMethods.credit.amount.toLocaleString()}
                  </strong>
                </span>
              </div>

              <div className="fx-cl spacem">
                <span>
                  <strong>Register Status</strong>
                </span>

                <span>
                  Opening Balance:{" "}
                  <strong>
                    ₦{(registerData?.openingBalance || 0).toLocaleString()}
                  </strong>
                </span>

                <span>
                  Cash Received:{" "}
                  <strong>
                    ₦{salesSummary.cashCollected.toLocaleString()}
                  </strong>
                </span>

                <span>
                  Expected Cash:{" "}
                  <strong>
                    ₦
                    {(
                      Number(registerData?.openingBalance || 0) +
                      salesSummary.cashCollected
                    ).toLocaleString()}
                  </strong>
                </span>

                <span>
                  Cash Difference:{" "}
                  <strong>
                    {registerData?.cashDifference == null ? "Not reconciled" : `₦${Number(registerData.cashDifference).toLocaleString()}`}
                  </strong>
                </span>
              </div>
            </div>

            <div className="fx-cl fx-ac fx-jc space1">
              <div className="fx-cl space1">
                <div className="g g2 space2">
                  <span>
                    <strong>Subtotal:</strong>
                  </span>
                  <span>
                    <strong>
                      ₦{salesSummary.totalPaymentsMade.toLocaleString()}
                    </strong>
                  </span>
                </div>

                <div className="g g2 space2">
                  <span>Sales tax:</span>
                  <span>₦{salesSummary.totalTax.toLocaleString()}</span>
                </div>

                <div className="g g2 space2">
                  <span>Discount: </span>
                  <span>₦{salesSummary.totalDiscount.toLocaleString()}</span>
                </div>
              </div>

              <div className="grandToltalPreviewSales g g2">
                <span>
                  <strong>Grand Total:</strong>
                </span>
                <span>
                  <strong>
                    {" "}
                    ₦{salesSummary.totalPaymentsMade.toLocaleString()}
                  </strong>
                </span>
              </div>

              <div className="fx-cl fx-ac fx-jc spacem">
                <span>Account Manager</span>
                <span>
                  <strong>
                    {registerData?.inventory?.manager?.firstName}{" "}
                    {registerData?.inventory?.manager?.lastName}
                  </strong>
                </span>
                <span>{/* <img src="" alt="signature" /> */}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
